/**
 * Hydrates error metadata at the catch point using available state and stack trace analysis
 */

import { DataProvider } from "@shared/constants/common";
import type { PaymentServiceBasket } from "../../services/useBasketPaymentService";
import PaymentHandler from "../../handlers/PaymentHandler";
import PaymentGateway from "../../gateways/PaymentGateway";
import type { PaymentErrorMetadata } from "./PaymentErrorMetadata";
import { PaymentOrchestrationError } from "./PaymentOrchestrationError";
import { PaymentConfigurationError } from "./PaymentConfigurationError";
import { PaymentInitializationError } from "./PaymentInitializationError";

export interface HydrationState {
  basket?: PaymentServiceBasket;
  basketType: DataProvider;
  handler?: PaymentHandler;
  gateway?: PaymentGateway;
  attemptNumber?: number;
}

/**
 * Hydrates error metadata from available state and error information
 */
export class ErrorContextHydrator {
  /**
   * Hydrates error metadata from current state
   */
  static hydrateMetadata(
    error: unknown,
    state: HydrationState,
    additionalMetadata?: Record<string, unknown>
  ): PaymentErrorMetadata {
    const metadata: PaymentErrorMetadata = {};

    // Extract basket information
    if (state.basket) {
      // basketId is a number in IBasket, but we store it as string in metadata
      metadata.basketId = state.basket.basketId?.toString() || null;

      if ("basketType" in state) {
        metadata.basketType = state.basketType;
      }

      if ("paymentVenue" in state.basket) {
        metadata.paymentVenue = state.basket.paymentVenue;
      }

      if ("purchaseSourcePlatform" in state.basket) {
        metadata.purchaseSourcePlatform = state.basket.purchaseSourcePlatform;
      }

      if ("paymentType" in state.basket) {
        metadata.paymentType = state.basket.paymentType;
      }
    }

    // Extract handler information
    if (state.handler) {
      metadata.handlerName = state.handler.constructor.name;
      metadata.handlerInitialized = state.handler.is_initialized ?? false;

      // If handler has a gateway
      if ("gateway" in state.handler && state.handler.gateway) {
        const gateway = state.handler.gateway as PaymentGateway;
        metadata.gatewayName =
          (gateway.constructor as typeof PaymentGateway).gatewayName ||
          gateway.constructor.name;
        metadata.gatewayType = gateway.constructor.name;
        // PaymentGateway doesn't track initialization state
        metadata.gatewayInitialized = undefined;
      }
    }

    // Direct gateway information (if provided separately)
    if (state.gateway) {
      metadata.gatewayName =
        (state.gateway.constructor as typeof PaymentGateway).gatewayName ||
        state.gateway.constructor.name;
      metadata.gatewayType = state.gateway.constructor.name;
      // PaymentGateway doesn't track initialization state
      metadata.gatewayInitialized = undefined;
    }

    // Recovery metadata
    if (state.attemptNumber) {
      metadata.attemptNumber = state.attemptNumber;
    }

    // Add error-type specific metadata
    if (error instanceof PaymentConfigurationError) {
      metadata.environment = process.env.NODE_ENV;

      // Try to detect missing config from error message
      if (error.message.toLowerCase().includes("stripe")) {
        metadata.missingConfig = "stripe_configuration";
      } else if (error.message.toLowerCase().includes("authorize")) {
        metadata.missingConfig = "authorize_configuration";
      }
    }

    if (error instanceof PaymentInitializationError) {
      metadata.handlerInitialized = state.handler?.is_initialized ?? false;

      // PaymentInitializationError specifically tracks handler initialization
      // Gateway initialization is not tracked as PaymentGateway doesn't have is_initialized
    }

    // Merge with any existing metadata on the error
    if (error instanceof PaymentOrchestrationError && error.metadata) {
      // Existing metadata takes precedence (it was explicitly set)
      return {
        ...metadata,
        ...error.metadata,
        ...additionalMetadata,
      };
    }

    // Merge with additional metadata
    return {
      ...metadata,
      ...additionalMetadata,
    };
  }

  /**
   * Applies hydrated metadata to an error if it's a PaymentOrchestrationError
   */
  static applyMetadata(
    error: unknown,
    state: HydrationState,
    additionalMetadata?: Record<string, unknown>
  ): void {
    if (error instanceof PaymentOrchestrationError) {
      const hydratedMetadata = this.hydrateMetadata(
        error,
        state,
        additionalMetadata
      );
      // eslint-disable-next-line no-param-reassign
      error.metadata = hydratedMetadata;
    }
  }
}
